import {
  ColumnSort,
  ORDER_VALUE,
  OrderValue,
  TableDataWithImage,
} from '@spo/logic';
import { Column, Header, HeaderGroup, flexRender } from '@tanstack/react-table';
import { VpCheckbox, VpIcon, VpIconName } from '@vtmn-play/react';
import clsx from 'clsx';
import './tableHeader.scss';

interface TableHeaderProps {
  headersGroup: HeaderGroup<TableDataWithImage>[];
  columnSort: ColumnSort;
  setColumnSort: (columnSort: ColumnSort) => void;
  globalChecked: boolean;
  setSelectAll: (status: boolean) => void;
}
export const TableHeader = ({
  headersGroup,
  columnSort,
  setColumnSort,
  globalChecked,
  setSelectAll,
  ...props
}: TableHeaderProps) => {
  const getOrderValue = (order: OrderValue) => {
    switch (order) {
      case ORDER_VALUE.EMPTY:
        return ORDER_VALUE.ASC;
      case ORDER_VALUE.ASC:
        return ORDER_VALUE.DESC;
      case ORDER_VALUE.DESC:
        return ORDER_VALUE.EMPTY;
      default:
        return ORDER_VALUE.EMPTY;
    }
  };

  const allowedSortColumn = [
    'id',
    'descriptionEn',
    'productNatureLabel',
    'startDate',
    'endDate',
    'productManagerLabel',
  ];

  const clickOnHeader = (column: Column<TableDataWithImage>) => {
    const idColumnMapped = column.id;
    if (allowedSortColumn.includes(idColumnMapped)) {
      const orderValue =
        idColumnMapped === columnSort.id ? columnSort.order : ORDER_VALUE.EMPTY;
      setColumnSort({ id: idColumnMapped, order: getOrderValue(orderValue) });
    }
  };

  const getArrow = (header: Header<TableDataWithImage, unknown>) => {
    let arrow: VpIconName;
    if (header.column.id === columnSort.id) {
      switch (columnSort.order) {
        case ORDER_VALUE.ASC:
          arrow = 'arrow-up';
          break;
        case ORDER_VALUE.DESC:
          arrow = 'arrow-down';
          break;
        default:
          arrow = 'arrow-up-down';
          break;
      }
    } else {
      arrow = 'arrow-up-down';
    }
    return arrow;
  };

  return (
    <thead {...props} className="table-sm_header">
      {headersGroup.map((headerGroup) => (
        <tr key={headerGroup.id}>
          <th className="cell" key="checkbox">
            <VpCheckbox
              data-testid="select-all-sm-checkbox"
              id="select-all-sm-checkbox"
              checked={globalChecked}
              onChange={() => {
                setSelectAll(!globalChecked);
              }}
            />
          </th>
          {headerGroup.headers.map((header) => (
            <th className="cell" key={header.id}>
              <button
                className={clsx('button-column', [
                  allowedSortColumn.includes(header.column.id)
                    ? 'cursor-pointer select-none flex'
                    : 'cursor-not-allowed',
                ])}
                tabIndex={0}
                onClick={() => {
                  clickOnHeader(header.column);
                }}
                onKeyDown={() => {
                  clickOnHeader(header.column);
                }}
                data-testid={`header-${header.column.id}`}
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
                {allowedSortColumn.includes(header.column.id) && (
                  <VpIcon
                    name={getArrow(header)}
                    size={16}
                    data-testid={getArrow(header)}
                  />
                )}
              </button>
            </th>
          ))}
        </tr>
      ))}
    </thead>
  );
};
